import { useRef, useEffect, type FC } from "react";

import { Button } from "@/shared/components/ui/button";
import { IconButton } from "@/shared/components/ui/icon-button";
import { Sheet, SheetContent, SheetTitle } from "@/shared/components/ui/sheet";
import { X } from "lucide-react";
import ContactProfilePicture from "@/shared/components/Contact/ContactProfilePicture";
import ContactFormFields from "@/shared/components/Contact/ContactFormFields";
import { ContactDetailView } from "./ContactDetailView";
import { useContactForm } from "@/shared/hooks/useContactForm";
import { useUserContacts } from "@/shared/hooks/useUserContacts";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { saveProfilePicture } from "@/shared/utils/saveProfilePicture";
import { trackJourneyEvent, EventType } from "@/shared/utils/core";
import { buildAddress } from "@/shared/utils/addressUtils";
import { resolveAddressLink } from "@/shared/utils/resolveAddressLink";
import { getDeceasedFirstName } from "@/shared/utils/contactLabels";
import { isValidEmail } from "@/shared/utils/validators";
import { sentryUtils } from "@/shared/utils/sentry";
import { enqueueSnackbar } from "notistack";

import type { PersonContact } from "@/shared/types/estateContact";

interface ContactDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  contact?: PersonContact | null;
  deceasedUserData?: {
    firstName?: string;
    displayName?: string;
    surName?: string;
  } | null;
  onContactAdded?: (contact: PersonContact) => void | Promise<void>;
  onContactUpdated?: (updated?: { id: number; email: string | null }) => void;
  initialEmailError?: string;
  initialLegalRole?: Array<{ value: string; label: string }>;
  initialRelationship?: string;
  hideAddress?: boolean;
  legalRoleEditable?: boolean;
  pendingInvitation?: { id: number; email: string } | null;
  acceptedInvitation?: { created_at: string } | null;
  onInvite?: (email: string, isAttorney?: boolean) => Promise<void>;
  onResendInvite?: (invitationId: number, email: string) => Promise<void>;
  onCancelInvite?: (invitationId: number) => Promise<void>;
  onRemoveAccess?: (email: string) => Promise<void>;
  isInviting?: boolean;
}

export const ContactDialog: FC<ContactDialogProps> = ({
  isOpen,
  onOpenChange,
  contact,
  deceasedUserData,
  onContactAdded,
  initialLegalRole,
  initialRelationship,
  pendingInvitation,
  acceptedInvitation,
  onInvite,
  onResendInvite,
  onCancelInvite,
  onRemoveAccess,
  isInviting,
}) => {
  const isEditMode = !!contact;

  const fileInputRef = useRef<HTMLInputElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  const { person, user } = useAppContext();
  const { createContactAsync, isCreating } = useUserContacts();

  const {
    formData,
    setFormData,
    errors,
    setErrors,
    isSubmitting,
    setIsSubmitting,
    profilePictureUrl,
    selectedFile,
    handleInputChange,
    handleAddressChange,
    handleProfilePictureSelect,
    uploadProfilePicture,
    resetForm,
  } = useContactForm(person?.estate_id);

  useEffect(() => {
    if (isOpen && !isEditMode) {
      const updates: Partial<typeof formData> = {};
      if (initialLegalRole && initialLegalRole.length > 0) updates.legalRole = initialLegalRole;
      if (initialRelationship) updates.relationship = initialRelationship;
      if (Object.keys(updates).length > 0) setFormData((prev) => ({ ...prev, ...updates }));
    } else if (!isOpen) {
      resetForm();
    }
  }, [isOpen, isEditMode, setFormData, resetForm, initialLegalRole, initialRelationship]);

  const handleSave = async () => {
    const addErrors: Record<string, string> = {};
    if (!formData.firstName.trim()) {
      addErrors.firstName = "First name is required";
    }
    if (formData.inviteToElayne && !formData.email.trim()) {
      addErrors.email = "Email is required to send an invitation";
    } else if (formData.email && !isValidEmail(formData.email)) {
      addErrors.email = "Enter a valid email address";
    }
    if (Object.keys(addErrors).length > 0) {
      setErrors(addErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      const firstName = formData.firstName.trim();
      const lastName = formData.lastName.trim();
      const addressString = buildAddress(formData.address);

      {
        const legalRoleValues = formData.legalRole
          .map((role) => role.value)
          .filter((value) => value !== "none");

        let profilePictureKey = null;

        if (selectedFile) {
          profilePictureKey = await uploadProfilePicture(selectedFile);
          if (!profilePictureKey) {
            console.error("Failed to upload profile picture");
            return;
          }
        }

        const savedContact = await createContactAsync({
          first_name: firstName,
          middle_name: formData.middleName.trim() || null,
          last_name: lastName || null,
          legal_role: legalRoleValues,
          relationship: formData.relationship || null,
          email: formData.email || null,
          phone: formData.phone || null,
          address: addressString || null,
          profile_picture: profilePictureKey,
        });

        if (savedContact) {
          void resolveAddressLink({
            raw: addressString,
            purpose: "mailing",
            entity: { contact_id: savedContact.id },
          });
        }

        trackJourneyEvent(EventType.CONTACT_ADDED, {
          relationship: formData.relationship,
          has_email: !!formData.email,
          has_phone: !!formData.phone,
          has_profile_picture: !!profilePictureKey,
          legal_roles: legalRoleValues?.length ?? 0,
        });

        if (profilePictureKey) {
          trackJourneyEvent(EventType.PROFILE_IMAGE_UPLOADED, {
            context: "contact_creation",
            contact_relationship: formData.relationship,
          });
        }

        if (profilePictureKey && savedContact) {
          await saveProfilePicture({
            fileKey: profilePictureKey,
            contactId: savedContact.id,
            userId: savedContact.user_id,
          });
        }

        window.dispatchEvent(
          new CustomEvent("contactUpdated", {
            detail: {
              contactId: savedContact.id,
              hasProfilePicture: !!profilePictureKey,
            },
          }),
        );

        if (formData.inviteToElayne && formData.email && savedContact && onInvite) {
          try {
            await onInvite(formData.email, formData.relationship === "attorney");
          } catch {
            enqueueSnackbar(
              "Contact added, but the invite couldn't be sent. You can retry from the detail panel.",
              { variant: "info" },
            );
          }
        }

        resetForm();

        if (onContactAdded && savedContact) {
          await onContactAdded(savedContact);
        } else {
          onOpenChange(false);
        }
      }
    } catch (error) {
      if (error instanceof Error && error.message.includes("email already exists")) {
        setErrors({ email: "A contact with this email already exists" });
      } else {
        console.error("Failed to save contact:", error);
        sentryUtils.captureError(error, "Failed to save contact", {
          userId: user?.user_id,
          estateId: person?.estate_id,
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const addModeFullName = [formData.firstName, formData.middleName, formData.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  const deceasedFirstName = getDeceasedFirstName(deceasedUserData);
  const isBusy = isSubmitting || isCreating;

  return (
    <>
      <Sheet open={isOpen} onOpenChange={onOpenChange}>
        <SheetContent
          ref={sheetRef}
          side="right"
          className="w-full sm:w-[600px] sm:max-w-[600px] p-0 overflow-y-auto bg-white flex flex-col"
          showClose={false}
        >
          {isEditMode && contact ? (
            <ContactDetailView
              contact={contact}
              deceasedUserData={deceasedUserData}
              onClose={() => onOpenChange(false)}
              pendingInvitation={pendingInvitation}
              acceptedInvitation={acceptedInvitation}
              onInvite={onInvite}
              onResendInvite={onResendInvite}
              onCancelInvite={onCancelInvite}
              onRemoveAccess={onRemoveAccess}
              isInviting={isInviting}
            />
          ) : (
            <>
              <div className="flex-1 overflow-y-auto p-[16px] sm:p-[32px]">
                <IconButton
                  className="absolute right-8 top-8"
                  label="Close"
                  tooltip={false}
                  onClick={() => onOpenChange(false)}
                >
                  <X className="h-4 w-4" />
                </IconButton>

                <div className="flex gap-6 items-center mb-6">
                  <ContactProfilePicture
                    profilePictureUrl={profilePictureUrl}
                    isLoadingPhoto={false}
                    fullName={addModeFullName}
                    onProfilePictureClick={() => fileInputRef.current?.click()}
                    fileInputRef={fileInputRef}
                    onProfilePictureSelect={handleProfilePictureSelect}
                    containerClassName="mb-0"
                  />
                  <SheetTitle className="text-[28px] font-semibold font-sans tracking-[-0.168px] leading-9">
                    {addModeFullName || "Add contact"}
                  </SheetTitle>
                </div>

                <ContactFormFields
                  formData={formData}
                  errors={errors}
                  onInputChange={handleInputChange}
                  onAddressChange={handleAddressChange}
                  relationshipLabel=""
                  deceasedFirstName={deceasedFirstName}
                  isMeContact={false}
                  isAddMode
                />
              </div>

              <div className="sticky bottom-0 border-t border-primary flex items-center justify-between p-4 bg-white">
                <Button variant="outline" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button variant="brand" onClick={handleSave} disabled={isBusy}>
                  {isBusy ? "Adding..." : "Add contact"}
                </Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
};
